/**
 * Session-storage helpers for "view mode" selectors (grid / list / calendar / …).
 *
 * Callers can pass their own `validModes` array so each page only accepts
 * the modes it actually renders. EventsPage uses `['grid','list','calendar']`;
 * FriendsPage uses the `['grid','list']` default.
 *
 * All functions are safe to call in non-browser / SSR contexts — they
 * short-circuit to the fallback when `window` is unavailable and swallow
 * storage errors (e.g. private-mode Safari).
 */

const DEFAULT_VALID_MODES = Object.freeze(["grid", "list"]);
const DEFAULT_VIEW_MODE = "grid";

const toValidSet = (validModes) => {
    if (!Array.isArray(validModes) || validModes.length === 0) {
        return new Set(DEFAULT_VALID_MODES);
    }
    return new Set(validModes.filter((mode) => typeof mode === "string" && mode.length > 0));
};

const normalizeFallback = (fallback, validSet) => (
    validSet.has(fallback)
        ? fallback
        : (validSet.has(DEFAULT_VIEW_MODE) ? DEFAULT_VIEW_MODE : validSet.values().next().value)
);

export const normalizeViewMode = (value, fallback = DEFAULT_VIEW_MODE, validModes = DEFAULT_VALID_MODES) => {
    const validSet = toValidSet(validModes);
    const safeFallback = normalizeFallback(fallback, validSet);
    return validSet.has(value) ? value : safeFallback;
};

export const readSessionViewMode = (
    storageKey,
    fallback = DEFAULT_VIEW_MODE,
    validModes = DEFAULT_VALID_MODES,
) => {
    const validSet = toValidSet(validModes);
    const safeFallback = normalizeFallback(fallback, validSet);

    if (typeof window === "undefined" || typeof storageKey !== "string" || !storageKey.trim()) {
        return safeFallback;
    }

    try {
        const stored = window.sessionStorage.getItem(storageKey);
        return validSet.has(stored) ? stored : safeFallback;
    } catch (_) {
        return safeFallback;
    }
};

export const writeSessionViewMode = (
    storageKey,
    value,
    fallback = DEFAULT_VIEW_MODE,
    validModes = DEFAULT_VALID_MODES,
) => {
    if (typeof window === "undefined" || typeof storageKey !== "string" || !storageKey.trim()) {
        return;
    }

    try {
        window.sessionStorage.setItem(
            storageKey,
            normalizeViewMode(value, fallback, validModes),
        );
    } catch (_) {
        // Best-effort persistence only.
    }
};
